

import { useState } from "react";
import SearchableDropdown from "../components/SearchableDropdown";
import CameraCapture from "@/components/CameraCapture";

interface TaskFormData {
  title: string;
  event: string;
  assignedTo: string;
  dueDate: string;
  priority: string;
  status: string;
  notes: string;
  before_image?: File | null;
  after_image?: File | null;
  before_preview?: string | null;
  after_preview?: string | null;
}

interface TaskFormProps {
  onSubmit: (data: TaskFormData) => void;
  onCancel: () => void;
  initialData?: Partial<TaskFormData>;
}

const priorities = ["high", "medium", "low"];
const statuses = ["pending", "in_progress", "completed"];

export default function TaskForm({ onSubmit, onCancel, initialData }: TaskFormProps) {
  const [formData, setFormData] = useState<TaskFormData>({
    title: initialData?.title || "",
    event: initialData?.event || "",
    assignedTo: initialData?.assignedTo || "",
    dueDate: initialData?.dueDate || "",
    priority: initialData?.priority || "medium",
    status: initialData?.status || "pending",
    notes: initialData?.notes || "",
    before_image: null,
    after_image: null,
    before_preview: null,
    after_preview: null,
  });

  const [errors, setErrors] = useState<Partial<Record<keyof TaskFormData, string>>>({});
  const [staffList, setStaffList] = useState(["Raj Sharma", "Neha Patil", "Amit Desai", "Arjun Mehta"]);
  const [eventsList, setEventsList] = useState([
    { id: "EVT-001", name: "Mehta Wedding" },
    { id: "EVT-002", name: "Sharma Corp Annual Day" },
    { id: "EVT-003", name: "Patel Birthday" },
    { id: "EVT-004", name: "Desai Wedding" },
    { id: "EVT-005", name: "Tech Summit 2026" },
  ]);

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof TaskFormData, string>> = {};
    
    if (!formData.title.trim()) newErrors.title = "Task title is required";
    if (!formData.assignedTo) newErrors.assignedTo = "Assignee is required";
    if (!formData.dueDate) newErrors.dueDate = "Due date is required";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onSubmit(formData);
    }
  };

  const handleChange = (field: keyof TaskFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const inputClass = (field: keyof TaskFormData) => `
    w-full px-3 py-2.5 rounded-xl border text-sm transition-colors
    ${errors[field] 
      ? "border-rose-300 bg-rose-50 focus:border-rose-400 focus:ring-2 focus:ring-rose-100" 
      : "border-stone-200 bg-white focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
    }
    outline-none
  `;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Task Title */}
        <div className="sm:col-span-2">
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Task Title *</label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => handleChange("title", e.target.value)}
            placeholder="e.g., Book floral decorator"
            className={inputClass("title")}
          />
          {errors.title && <p className="text-xs text-rose-500 mt-1">{errors.title}</p>}
        </div>

        {/* Event */}
        <div className="sm:col-span-2">
          <SearchableDropdown
            label="Related Event"
            options={eventsList.map(ev => ({ value: ev.id, label: `${ev.id} - ${ev.name}` }))}
            value={formData.event}
            onChange={(value) => handleChange("event", value)}
            placeholder="Select or search event (optional)"
            allowAddNew
            onAddNew={(value) => {
              const newEvent = { id: value, name: value };
              setEventsList(prev => [...prev, newEvent]);
              handleChange("event", value);
            }}
          />
        </div>

        {/* Assigned To */}
        <div>
          <SearchableDropdown
            label="Assigned To *"
            options={staffList.map(s => ({ value: s, label: s }))}
            value={formData.assignedTo}
            onChange={(value) => handleChange("assignedTo", value)}
            placeholder="Select or type assignee"
            allowAddNew
            onAddNew={(value) => {
              setStaffList(prev => [...prev, value]);
              handleChange("assignedTo", value);
            }}
            error={errors.assignedTo}
          />
        </div>

        {/* Due Date */}
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Due Date *</label>
          <input
            type="date"
            value={formData.dueDate}
            onChange={(e) => handleChange("dueDate", e.target.value)}
            className={inputClass("dueDate")}
          />
          {errors.dueDate && <p className="text-xs text-rose-500 mt-1">{errors.dueDate}</p>}
        </div>

        {/* Priority */}
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Priority</label>
          <div className="flex gap-2">
            {priorities.map(p => (
              <button
                key={p}
                type="button"
                onClick={() => handleChange("priority", p)}
                className={`flex-1 py-2 rounded-xl text-xs font-semibold transition-colors ${
                  formData.priority === p
                    ? p === 'high' ? 'bg-rose-100 text-rose-700 border border-rose-200' :
                      p === 'medium' ? 'bg-amber-100 text-amber-700 border border-amber-200' :
                      'bg-stone-100 text-stone-600 border border-stone-200'
                    : 'bg-stone-50 text-stone-400 border border-stone-200 hover:bg-stone-100'
                }`}
              >
                {p === 'high' ? '🔴' : p === 'medium' ? '🟡' : '⚪'} {p.charAt(0).toUpperCase() + p.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Status */}
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Status</label>
          <select
            value={formData.status}
            onChange={(e) => handleChange("status", e.target.value)}
            className={inputClass("status")}
          >
            {statuses.map(s => (
              <option key={s} value={s}>
                {s === 'in_progress' ? 'In Progress' : s.charAt(0).toUpperCase() + s.slice(1)}
              </option>
            ))}
          </select>
        </div>

        {/* Notes */}
        <div className="sm:col-span-2">
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Notes</label>
          <textarea
            value={formData.notes}
            onChange={(e) => handleChange("notes", e.target.value)}
            placeholder="Additional details or instructions..."
            rows={3}
            className={`${inputClass("notes")} resize-none`}
          />
        </div>

        {/* Task Photos Section */}
        <div className="sm:col-span-2">
          <label className="block text-xs font-medium text-stone-500 mb-3">Task Photos (Camera)</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <CameraCapture
              label="Before Photo"
              description="Capture photo before task"
              onCapture={(file, preview) => {
                setFormData(prev => ({
                  ...prev,
                  before_image: file,
                  before_preview: preview,
                }));
              }}
            />
            <CameraCapture
              label="After Photo"
              description="Capture photo after completion"
              onCapture={(file, preview) => {
                setFormData(prev => ({
                  ...prev,
                  after_image: file,
                  after_preview: preview,
                }));
              }}
            />
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-4 border-t border-stone-100">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-4 py-2.5 rounded-xl bg-stone-100 text-stone-600 font-semibold text-sm hover:bg-stone-200 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="flex-1 px-4 py-2.5 rounded-xl text-white font-semibold text-sm gold-gradient hover:opacity-90 transition-opacity"
        >
          {initialData ? "Update Task" : "Create Task"}
        </button>
      </div>
    </form>
  );
}
